// Staff identity documents and the admin API inputs (docs/clairvoyance/07-security.md §7.1, §7.2).
import { z } from 'zod';
import { PERMISSIONS } from '@ecobox/domain';
import { codeSchema } from './command';

/** Staff sign in with a username; Auth sees `{username}@staff.eco-box.app` (07 §7.1). */
export const STAFF_EMAIL_DOMAIN = 'staff.eco-box.app';

export const usernameSchema = z
  .string()
  .regex(/^[a-z][a-z0-9._-]{2,30}$/, 'Username: 3-31 characters, lower case, starts with a letter');
export const passwordSchema = z.string().min(10, 'Password needs at least 10 characters').max(128);
export const roleIdSchema = z.string().regex(/^[a-z][a-z0-9_]{1,40}$/, 'Invalid role id');
export const displayNameSchema = z.string().trim().min(1).max(80);
export const uidSchema = z.string().min(1).max(128);

export function staffEmail(username: string): string {
  return `${username}@${STAFF_EMAIL_DOMAIN}`;
}

/** `all`, or sector and branch codes. Empty lists mean no access (07 §7.2). */
export const scopeSchema = z.union([
  z.literal('all'),
  z
    .object({
      sectors: z.array(codeSchema).max(60),
      branches: z.array(codeSchema).max(120),
    })
    .strict(),
]);

export const permissionSchema = z.enum(PERMISSIONS);

/** `roles/{id}` */
export const roleDocSchema = z.object({
  name: z.string().min(1).max(80),
  permissions: z.array(z.string()),
});

/** `staff/{uid}` */
export const staffProfileSchema = z.object({
  username: usernameSchema,
  displayName: z.string(),
  roleIds: z.array(roleIdSchema),
  scope: scopeSchema,
  active: z.boolean(),
  permsVersion: z.number().int().nonnegative(),
  /**
   * The password was set by someone else (an agent's temporary password, 07 §7.2): the person
   * chooses their own before they can do anything (`changeOwnPassword`).
   */
  mustChangePassword: z.boolean().optional(),
  /**
   * `bot`: the account behind an AI worker connection (Grok; functions/src/bots/account.ts). It has
   * no sign-in. People have no `kind`. "Agents" in EcoBox are the commission-paid people who bring
   * cargo; AI accounts are always called AI workers (21 §21.1).
   */
  kind: z.literal('bot').optional(),
});
export type StaffProfile = z.infer<typeof staffProfileSchema>;

/** Custom claims on a staff Auth user. `scope: "doc"` means rules read `staff/{uid}` (07 §7.2). */
export interface StaffClaims {
  staff: true;
  active: boolean;
  roleIds: string[];
  scope: z.infer<typeof scopeSchema> | 'doc';
}

const roleIdsSchema = z.array(roleIdSchema).min(1).max(10);

export const bootstrapInputSchema = z
  .object({
    token: z.string().min(1).max(256),
    username: usernameSchema,
    password: passwordSchema,
    displayName: displayNameSchema.optional(),
  })
  .strict();

export const createStaffInputSchema = z
  .object({
    username: usernameSchema,
    password: passwordSchema,
    displayName: displayNameSchema,
    roleIds: roleIdsSchema,
    scope: scopeSchema,
  })
  .strict();

export const updateStaffInputSchema = z
  .object({
    uid: uidSchema,
    displayName: displayNameSchema.optional(),
    roleIds: roleIdsSchema.optional(),
    scope: scopeSchema.optional(),
    active: z.boolean().optional(),
    password: passwordSchema.optional(),
  })
  .strict();

export const deactivateStaffInputSchema = z.object({ uid: uidSchema }).strict();

/** A signed-in person choosing their own password (after a temporary one). */
export const changeOwnPasswordInputSchema = z.object({ password: passwordSchema }).strict();

export type BootstrapInput = z.infer<typeof bootstrapInputSchema>;
export type CreateStaffInput = z.infer<typeof createStaffInputSchema>;
export type UpdateStaffInput = z.infer<typeof updateStaffInputSchema>;
export type DeactivateStaffInput = z.infer<typeof deactivateStaffInputSchema>;
export type ChangeOwnPasswordInput = z.infer<typeof changeOwnPasswordInputSchema>;
